import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import IProcedurePublishApprovalResult from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedurePublishApprovalResult';
import React, { useState } from 'react';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { getService } from '@Client/runner.hooks/useService';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import IPublishProcedure from '@Shared.Angular/@types/core/contracts/commands/procedure/publishProcedure';
import { SmartSelect } from '@Shared.Angular/flowingly.components/smartselect/SmartSelect/SmartSelect';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import { SearchEntityType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import KendoReactDatePicker from '@Shared.Angular/kendo.react/KendoReactDatePicker';
import { ApiDateTime } from '@Shared.Angular/@types/dateTime';

type Props = {
  procedure: IProcedureDetail;
  isPublishRequest?: boolean;
  onClose: () => void;
  onPublishOrRequest: (procedure: IProcedureDetail) => void;
};

const PublishProcedureModal = ({
  procedure,
  isPublishRequest = false,
  onClose,
  onPublishOrRequest
}: Props) => {
  const procedureApiService = getService('procedureApiService');
  const flowinglyConstants = getService('flowinglyConstants');
  const notificationService = getService('notificationService');
  const enablePublishNotificationOption =
    getService('APP_CONFIG').enablePublishNotificationOption;
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [categories, setCategories] = useState<ICategoryDetail[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<ICategoryDetail | null>(null);

  const [publishTo, setPublishTo] = useState<PublishType>(PublishType.EVERYONE);
  const [selectedActors, setSelectedActors] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [publishedToActors, setPublishedToActors] = useState<
    IFlowinglySearchResult[] | null
  >(null);
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notifyUsersComment, setNotifyUsersComment] = useState('');
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [processReviewDate, setProcessReviewDate] = useState<Date | null>(null);

  React.useEffect(() => {
    const fetchCategories = procedureApiService.getCategoriesUserCanPublishTo(
      procedure.id,
      isPublishRequest
    );
    const $q = getService('$q');
    const fetchPublishedTo = isPublishRequest
      ? $q.resolve<null>(null)
      : procedureApiService.getProcedurePublishedTo(procedure.id);
    const fetchPublishRequest = isPublishRequest
      ? procedureApiService.getProcedurePublishRequest(procedure.id)
      : $q.resolve<null>(null);

    Promise.all([fetchCategories, fetchPublishedTo, fetchPublishRequest])
      .then(
        ([fetchedCategories, fetchedPublishedTo, fetchedPublishRequest]) => {
          const matchedCategory = getService('categoryApiService').findCategory(
            fetchedPublishRequest?.categoryId || procedure.categoryId,
            fetchedCategories
          );
          const existingProcessReviewDate = procedure.processReviewDateUtc;

          if (existingProcessReviewDate) {
            setProcessReviewDate(new Date(`${existingProcessReviewDate}Z`));
          }

          setSelectedCategory(matchedCategory);
          setCategories(fetchedCategories);
          if (isPublishRequest) {
            setNotifyUsers(fetchedPublishRequest.notifyUsers);
            if (fetchedPublishRequest.publishToEveryone) {
              setPublishedToActors([]);
            } else {
              setPublishedToActors(
                fetchedPublishRequest.users.concat(fetchedPublishRequest.groups)
              );
              setPublishTo(PublishType.SPECIFIC_ACTORS);
            }
          } else {
            const isPublishedToEveryone =
              fetchedPublishedTo.length === 1 &&
              fetchedPublishedTo[0].searchEntityType ===
                SearchEntityType.GROUP &&
              fetchedPublishedTo[0].displayName ===
                flowinglyConstants.EVERYONE_GROUP_NAME;
            if (isPublishedToEveryone) {
              setPublishedToActors([]);
            } else {
              setPublishedToActors(fetchedPublishedTo);
              if (fetchedPublishedTo.length > 0) {
                setPublishTo(PublishType.SPECIFIC_ACTORS);
              }
            }
          }
        }
      )
      .catch((error) => {
        if (isPublishRequest && error?.status === 404) {
          notificationService.showErrorToast(
            'This publish request has already been approved'
          );
        }
        onClose();
      });
  }, []);

  // Clear comment when switching to Unpublish or when creating a publish request (non-publisher)
  React.useEffect(() => {
    if (
      publishTo === PublishType.UNPUBLISH ||
      (!procedure.userCanPublish && !isPublishRequest)
    ) {
      setNotifyUsersComment('');
    }
  }, [publishTo, procedure.userCanPublish, isPublishRequest]);

  const publishProcedure = () => {
    if (!isSelectedCategoryValid() || !isActorSelectionValid()) {
      return;
    }
    setIsRequestPending(true);
    const userIds =
      publishTo === PublishType.SPECIFIC_ACTORS
        ? selectedActors
            .filter((actor) => actor.searchEntityType === SearchEntityType.USER)
            .map((actor) => actor.id)
        : [];

    const groupIds =
      publishTo === PublishType.SPECIFIC_ACTORS
        ? selectedActors
            .filter(
              (actor) => actor.searchEntityType === SearchEntityType.GROUP
            )
            .map((actor) => actor.id)
        : [];

    const command: IPublishProcedure = {
      id: procedure.id,
      categoryId: selectedCategory.id,
      notifyUsers: notifyUsers,
      publishToEveryone: publishTo === PublishType.EVERYONE,
      userIds: userIds,
      groupIds: groupIds,
      approvalDueUtc: dueDate ? (dueDate.toISOString() as ApiDateTime) : null,
      processReviewDateUtc:
        publishTo === PublishType.UNPUBLISH
          ? null
          : processReviewDate
          ? (processReviewDate.toISOString() as ApiDateTime)
          : null
    };

    // Do not include a comment when performing an Unpublish action
    if (
      procedure.userCanPublish &&
      notifyUsers &&
      publishTo !== PublishType.UNPUBLISH
    ) {
      command.comment = notifyUsersComment;
    }

    if (procedure.userCanPublish && !isPublishRequest) {
      procedureApiService
        .publishProcedure(command)
        .then((publishedProcedure) => {
          onPublishOrRequest(publishedProcedure);
          notificationService.showSuccessToast(
            'Procedure published successfully'
          );
        })
        .finally(() => {
          setIsRequestPending(false);
        });
    } else if (procedure.userCanPublish && isPublishRequest) {
      procedureApiService
        .approveProcedurePublishRequest(command)
        .then((approvalResult: IProcedurePublishApprovalResult) => {
          if (!approvalResult.isActive) {
            notificationService.showErrorToast(
              'This publish request has already been approved'
            );
            onClose();
            return;
          }

          if (!approvalResult.procedure) {
            notificationService.showErrorToast('Unable to publish procedure');
            onClose();
            return;
          }

          onPublishOrRequest(approvalResult.procedure);
          notificationService.showSuccessToast(
            'Procedure published successfully'
          );
        })
        .finally(() => {
          setIsRequestPending(false);
        });
    } else {
      procedureApiService
        .createProcedurePublishRequest(command)
        .then((procedure) => {
          onPublishOrRequest(procedure);
          notificationService.showSuccessToast('Publish request sent');
        })
        .finally(() => {
          setIsRequestPending(false);
        });
    }
  };

  const categorySelected = (
    id: Guid,
    index: string,
    name: string,
    item: ICategoryDetail
  ) => {
    setSelectedCategory(item);
  };

  const notifyUsersChanged = (checked: boolean) => {
    setNotifyUsers(checked);
    if (!checked) {
      setNotifyUsersComment('');
    }
  };

  const isSelectedCategoryValid = () => {
    return selectedCategory !== null && selectedCategory.id !== EmptyGuid;
  };

  const isActorSelectionValid = () => {
    return (
      publishTo !== PublishType.SPECIFIC_ACTORS || selectedActors.length > 0
    );
  };

  const isProcessReviewDateOverdue = () => {
    if (publishTo === PublishType.UNPUBLISH || processReviewDate === null) {
      return false;
    }

    const selectedDate = new Date(processReviewDate);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selectedDate < today;
  };

  const isReadyToPublish = () => {
    return (
      !isRequestPending &&
      isSelectedCategoryValid() &&
      isActorSelectionValid() &&
      (procedure.userCanPublish || selectedCategory.approverId)
    );
  };

  const actionButtonText = !procedure.userCanPublish
    ? 'Send for Publish Approval'
    : publishTo === PublishType.UNPUBLISH
    ? 'Unpublish'
    : 'Publish';

  if (publishedToActors === null) {
    return <></>;
  }

  return (
    <Modal
      title="Publish SOP"
      id="publish-procedure-modal"
      actionButtonText={actionButtonText}
      isActionDisabled={!isReadyToPublish()}
      onActionClick={publishProcedure}
      onCancelClick={onClose}
      onClose={onClose}
      size="md"
    >
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <div className="row-title">Publish Category</div>
      <div className="category">
        <label>The category users will find the SOP in </label>
        <div className="category-dropdown">
          <KendoReactDropDownTree
            placeholder="Select Category..."
            categories={categories}
            className="modal-overlay"
            categoryClicked={categorySelected}
            dataItemKey="id"
            textField="name"
            subItemsField="subCategories"
            selectedId={selectedCategory?.id || EmptyGuid}
            showFilter={true}
          />
          {!isSelectedCategoryValid() && (
            <div className="error-msg">Please select a category</div>
          )}
          {!procedure.userCanPublish &&
            selectedCategory &&
            !selectedCategory.approverId && (
              <div className="error-msg">
                The category doesn't have an approver
              </div>
            )}
        </div>
      </div>
      {enablePublishNotificationOption && (
        <div className="notify-users-section">
          <div className="notify-container">
            <div>
              <input
                id="notifyUser"
                type="checkbox"
                checked={notifyUsers}
                disabled={publishTo === PublishType.UNPUBLISH}
                onChange={(e) => notifyUsersChanged(e.target.checked)}
              />
              <label htmlFor="notifyUser"></label>
            </div>
            <div title="All users and members of teams the SOP is published to will be sent an email">
              Notify users <b>{procedure.name}</b> has been published?
            </div>
          </div>
          {notifyUsers &&
            publishTo !== PublishType.UNPUBLISH &&
            (procedure.userCanPublish || isPublishRequest) && (
              <div className="notify-users-comment">
                <label
                  className="row-title optional"
                  htmlFor="notify-users-comment"
                >
                  Comment
                </label>
                <textarea
                  id="notify-users-comment"
                  data-testid="notify-users-comment"
                  placeholder="Provide an optional comment"
                  value={notifyUsersComment}
                  onChange={(e) => setNotifyUsersComment(e.target.value)}
                />
              </div>
            )}
        </div>
      )}
      <div className="row-title">Publish To</div>
      <div>
        <input
          name="radio1"
          id="radios1"
          type="radio"
          radioGroup="publishTo"
          onChange={() => setPublishTo(PublishType.EVERYONE)}
          checked={publishTo === PublishType.EVERYONE}
        />
        <label htmlFor="radios1">
          Everyone
          <span className="label-description">
            Everyone in this organisation can view this SOP
          </span>
        </label>
      </div>
      <div>
        <input
          name="radio2"
          id="radios2"
          type="radio"
          radioGroup="publishTo"
          onChange={() => setPublishTo(PublishType.SPECIFIC_ACTORS)}
          checked={publishTo === PublishType.SPECIFIC_ACTORS}
        />
        <label htmlFor="radios2">
          Specific Users & Teams
          <span className="label-description">
            Choose who has access to this SOP
          </span>
        </label>
      </div>
      {procedure.published && procedure.userCanPublish && !isPublishRequest && (
        <div>
          <input
            name="radio3"
            id="radios3"
            type="radio"
            radioGroup="publishTo"
            onChange={() => setPublishTo(PublishType.UNPUBLISH)}
            checked={publishTo === PublishType.UNPUBLISH}
          />
          <label htmlFor="radios3">
            Unpublish this SOP
            <span className="label-description">
              Nobody will be able to view this SOP
            </span>
          </label>
        </div>
      )}
      {publishTo === PublishType.SPECIFIC_ACTORS && (
        <SmartSelect
          headerText="Publish List"
          infoText="List of people who are able to view this SOP"
          initialSelectedItems={publishedToActors}
          onChange={setSelectedActors}
        />
      )}
      {procedure.userCanPublish && (
        <div id="procedure-process-review-date">
          <label className="row-title">Process review date</label>
          <KendoReactDatePicker
            id="procedure-process-review-date-picker"
            data-testid="procedure-process-review-date-picker"
            format={'dd/MM/yyyy'}
            formatPlaceholder={{ day: 'DD', month: 'MM', year: 'YYYY' }}
            value={processReviewDate}
            disabled={publishTo === PublishType.UNPUBLISH}
            onChange={(event) => setProcessReviewDate(event.value)}
          />
          {isProcessReviewDateOverdue() && (
            <div className="error-msg">Review date is overdue</div>
          )}
        </div>
      )}
      {!procedure.userCanPublish && (
        <div id="procedure-approval-date">
          <label className="optional">Due Date for Approval</label>
          <KendoReactDatePicker
            id="procedure-approval-date-picker"
            format={'dd/MM/yyyy'}
            value={dueDate}
            onChange={(event) => setDueDate(event.value)}
          />
        </div>
      )}
    </Modal>
  );
};

export default PublishProcedureModal;

enum PublishType {
  EVERYONE,
  SPECIFIC_ACTORS,
  UNPUBLISH
}
